/**
 * Guards React against browser page translation (FUN-5044).
 *
 * Chrome/Google Translate replaces text nodes with `<font>` wrappers, detaching nodes React still
 * holds references to. The next reconciliation then calls `insertBefore`/`removeChild` with a
 * detached node, throws, and the error boundary takes the whole page down (seen on
 * /partner/activities: "Failed to execute 'insertBefore' on 'Node'").
 *
 * This is the mitigation recommended by the React team while the underlying issue stays open:
 * https://github.com/facebook/react/issues/11538#issuecomment-417504600 — make the two DOM calls
 * tolerate a translated child instead of throwing. Layout-wise the stale node was already replaced
 * by the translation, so skipping the operation (or appending, for insertBefore) matches what the
 * user sees.
 *
 * Imported for its side effect from GlobalContexts so it runs before React mounts. No-ops on the
 * server and when the page is not being translated.
 */

const warnOnce = (() => {
    let warned = false;
    return (method: string) => {
        if (warned) return;
        warned = true;
        console.warn(
            `[translation-dom-guard] Suppressed ${method} on a node detached by page translation.`,
        );
    };
})();

if (typeof Node === 'function' && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
        if (child.parentNode !== this) {
            warnOnce('removeChild');
            return child;
        }
        return originalRemoveChild.call(this, child) as T;
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function insertBefore<T extends Node>(
        this: Node,
        newNode: T,
        referenceNode: Node | null,
    ): T {
        if (referenceNode && referenceNode.parentNode !== this) {
            warnOnce('insertBefore');
            // The reference was detached by translation; appending puts the new node where the
            // reference used to sit (at the end of this parent's React-managed children).
            return originalInsertBefore.call(this, newNode, null) as T;
        }
        return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
}

export {};
